import {
  HTTP_STATUS_FORBIDDEN,
  HTTP_STATUS_INTERNAL_SERVER_ERROR,
  HTTP_STATUS_OK,
} from '../../../constants/api'
import { EMPTY_LIST, EMPTY_OBJECT } from '../../../constants/common'
import {
  RENTAL_PORTAL_MOCK_LATENCY,
  RENTAL_PORTAL_MOCK_STATE,
} from '../../../constants/rentalPortalMockState'
import { isApiSuccessStatus } from '../../../utils/api'
import {
  APPLICANT_DETAILS_SNAPSHOT,
  APPLICANTS_SNAPSHOT,
  LISTINGS_SNAPSHOT,
} from '../../../utils/rentalPortalMockData'
import {
  getRentalPortalMockCompanyFlags,
  hasRentalPortalMockCreateRight,
  hasRentalPortalMockState,
} from '../../../utils/rentalPortalMockState'
import {
  APPLICANT_STAGE,
  APPLICANTS_FILTER_PARAM,
  getCreatedMockApplicants,
  getIsMockApplicantArchived,
  getMockApplicantMatch,
  setMockApplicantArchived,
} from '../../RentalPortalApplicant'

import { APPLICANT_MASS_ACTION } from './RentalPortalApplicants.constants'
import {
  getApplicantsColumns,
  getApplicantsPayloadColumns,
  getIsApplicantArchivable,
} from './RentalPortalApplicants.utils'

export const MOCK_APPLICANTS_PERMISSIONS = {
  can_approve:
    hasRentalPortalMockCreateRight() &&
    hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.canApprove),
  can_export: true,
  can_manage: hasRentalPortalMockCreateRight(),
}

const MOCK_APPLICANT_LISTING_OWNER_BY_ID = new Map(
  LISTINGS_SNAPSHOT.map(listing => [listing.id, listing.responsible_user])
)

const MOCK_APPLICANT_FACET_MATCH = {
  [APPLICANTS_FILTER_PARAM.awaitingMyApproval]:
    getIsMockApplicantAwaitingMyApproval,
  [APPLICANTS_FILTER_PARAM.eligibility]: (applicant, value) =>
    getMockApplicantMatch(applicant)?.eligibility === value,
  [APPLICANTS_FILTER_PARAM.listing]: (applicant, value) =>
    String(applicant.listing_id) === String(value),
  [APPLICANTS_FILTER_PARAM.needsMyAction]: applicant =>
    applicant.attention?.needs_you === true,
  [APPLICANTS_FILTER_PARAM.receivedGte]: (applicant, value) =>
    applicant.received >= value,
  [APPLICANTS_FILTER_PARAM.receivedLte]: (applicant, value) =>
    applicant.received <= value,
  [APPLICANTS_FILTER_PARAM.responsibleUser]: (applicant, value) =>
    String(MOCK_APPLICANT_LISTING_OWNER_BY_ID.get(applicant.listing_id)?.id) ===
    String(value),
  [APPLICANTS_FILTER_PARAM.source]: (applicant, value) =>
    applicant.source === value,
  [APPLICANTS_FILTER_PARAM.stage]: (applicant, value) =>
    applicant.stage === value,
}

function getFilteredMockApplicants(applicants, filterValues) {
  return applicants.filter(applicant =>
    Object.entries(filterValues ?? EMPTY_OBJECT).every(
      ([param, value]) =>
        !value ||
        !MOCK_APPLICANT_FACET_MATCH[param] ||
        MOCK_APPLICANT_FACET_MATCH[param](applicant, value)
    )
  )
}

const MOCK_APPLICANTS_LISTING_FLAT = new Map(
  LISTINGS_SNAPSHOT.map(listing => [listing.id, listing.flat])
)

function getMockApplicantsSource() {
  return hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.empty)
    ? EMPTY_LIST
    : [...getCreatedMockApplicants(), ...APPLICANTS_SNAPSHOT]
        .filter(applicant => !getIsMockApplicantArchived(applicant))
        .map(applicant => ({
          ...applicant,
          flat: MOCK_APPLICANTS_LISTING_FLAT.get(applicant.listing_id) ?? null,
        }))
}

const MOCK_APPLICANT_SESSION_USER_ID = 77

const MOCK_APPLICANT_SELECTED_BY_USER_ID = new Map(
  APPLICANT_DETAILS_SNAPSHOT.records.map(record => [
    record.id,
    record.selected_by_user_id,
  ])
)

const MOCK_APPLICANTS_CAN_APPROVE =
  MOCK_APPLICANTS_PERMISSIONS.can_approve ||
  (MOCK_APPLICANTS_PERMISSIONS.can_manage &&
    hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.allRights))

const MOCK_APPLICANTS_AWAITING_APPROVAL_OVERFLOW_COUNT = 128

function getIsMockApplicantAwaitingMyApproval(applicant) {
  return (
    applicant.stage === APPLICANT_STAGE.selected &&
    MOCK_APPLICANT_SELECTED_BY_USER_ID.get(applicant.id) !==
      MOCK_APPLICANT_SESSION_USER_ID
  )
}

export function getMockApplicantsAwaitingApprovalCount() {
  if (!MOCK_APPLICANTS_CAN_APPROVE) {
    return 0
  }

  return hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.bulk)
    ? MOCK_APPLICANTS_AWAITING_APPROVAL_OVERFLOW_COUNT
    : getMockApplicantsSource().filter(getIsMockApplicantAwaitingMyApproval)
        .length
}

export function getMockApplicantsDefaultColumns() {
  return Promise.resolve(
    getApplicantsPayloadColumns({
      columns: getApplicantsColumns(),
      hasAttention: !hasRentalPortalMockState(
        RENTAL_PORTAL_MOCK_STATE.noAttention
      ),
      hasFitScore: getRentalPortalMockCompanyFlags().fit_score,
      hasMatch: !hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.noMatching),
    })
  )
}

const MOCK_APPLICANT_MASS_ACTION_WRITE_DELAY = 400

const MOCK_APPLICANTS_MASS_ACTION_ERROR = {
  response: { status: HTTP_STATUS_INTERNAL_SERVER_ERROR },
}

function withMockApplicantsMassActionLatency() {
  return new Promise(resolve => {
    setTimeout(
      resolve,
      hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.massActionError) ||
        hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.massActionPartial)
        ? RENTAL_PORTAL_MOCK_LATENCY
        : MOCK_APPLICANT_MASS_ACTION_WRITE_DELAY
    )
  })
}

function getMockApplicantsMassActionTargets({ all, filters }) {
  const { exclude_id: excludeId, id: ids, ...facets } = filters

  const scoped = getFilteredMockApplicants(getMockApplicantsSource(), facets)

  return all
    ? scoped.filter(applicant => !excludeId.includes(applicant.id))
    : scoped.filter(applicant => ids.includes(applicant.id))
}

function getMockApplicantsMassActionStatusCode({ applicant, index }) {
  if (!getIsApplicantArchivable(applicant)) {
    return HTTP_STATUS_FORBIDDEN
  }

  return hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.massActionPartial) &&
    index % 2 === 1
    ? HTTP_STATUS_INTERNAL_SERVER_ERROR
    : HTTP_STATUS_OK
}

const mockApplicantsMassActionRequests = []

export function getMockApplicantsMassActionRequests() {
  return mockApplicantsMassActionRequests
}

export function performMockApplicantsMassAction(request) {
  mockApplicantsMassActionRequests.push(request)

  console.debug('[RP mock] POST applicants/mass-action/', request)

  return withMockApplicantsMassActionLatency().then(() => {
    if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.massActionError)) {
      return Promise.reject(MOCK_APPLICANTS_MASS_ACTION_ERROR)
    }

    const { action, all, filters } = request

    const items = getMockApplicantsMassActionTargets({ all, filters }).map(
      (applicant, index) => ({
        id: applicant.id,
        status_code: getMockApplicantsMassActionStatusCode({
          applicant,
          index,
        }),
      })
    )

    const successIds = items
      .filter(item => isApiSuccessStatus(item.status_code))
      .map(item => item.id)

    successIds.forEach(id => {
      setMockApplicantArchived({
        archived: action === APPLICANT_MASS_ACTION.archive,
        id,
      })
    })

    return {
      data: { items, success_ids: successIds },
      status: HTTP_STATUS_OK,
    }
  })
}
